<template>
  <ContentTemplate title="Google Drive" icon="google-drive.svg" :container-size-small="true">
    <template v-if="isConnected">
      <div class="google-drive-view">
        <GoogleDriveDescription />
        <div class="google-drive-view__browser-header">
          <h5>Import from Google Drive</h5>
          <p>Select a project, then the files to import. Each file becomes a project document, exactly as if it had been uploaded in the chat.</p>
          <select v-model="selectedProjectId" class="google-drive-view__project">
            <option value="" disabled>Select a project</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <DriveBrowser v-if="selectedProjectId" :project-id="selectedProjectId" @file-imported="onFileImported" />
        <div class="google-drive-view__disconnect">
          <ButtonElement class="btn--ms btn--neutral btn--sm" @clickOnButton="openDisconnectPopup">
            <fa :icon="['far', 'plug-circle-xmark']" />
            Disconnect Google Drive
          </ButtonElement>
        </div>
      </div>
    </template>

    <template v-else>
      <div class="google-drive-view">
        <GoogleDriveDescription />
        <div class="google-drive-view__actions">
          <ButtonElement
            class="btn--ms btn--accent btn--md"
            @clickOnButton="connectGoogleDrive"
          >
            Connect Google Drive
          </ButtonElement>
        </div>
      </div>
    </template>
  </ContentTemplate>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { ContentTemplate } from '@shared/components'
import ButtonElement from '@shared/components/elements/button/button.element.vue'
import { useWorkspaceStore } from '@shared/stores/workspace.store'
import { useIntegrationStore } from '@domains/settings/stores/integration.store'
import { useGeneralStore } from '@shared/stores/general.store'
import { IntegrationNs } from '@shared/types'
import type { PopupNs } from '@shared/types'
import GoogleDriveDescription from './google-drive-description.vue'
import DriveBrowser from './drive-browser.vue'
import { useProjectStore } from '@domains/dashboard/stores/project.store'
import type { ProjectNs } from '@shared/types'

export default defineComponent({
  name: 'GoogleDriveView',
  components: {
    ContentTemplate,
    ButtonElement,
    GoogleDriveDescription,
    DriveBrowser,
  },
  data() {
    return {
      selectedProjectId: '' as string,
      projects: [] as ProjectNs.Project[],
    }
  },
  async mounted() {
    try {
      const wsStore = useWorkspaceStore()
      this.projects = await useProjectStore().loadAllProjectsInWorkspace({ workspaceId: wsStore.getSelectedWorkspaceId })
    } catch {
      this.projects = []
    }
  },
  computed: {
    isConnected(): boolean {
      const intStore = useIntegrationStore()
      return !!intStore.getWorkspaceIntegrations.find(
        (i: IntegrationNs.IntegrationInfo) =>
          i.integrationType === IntegrationNs.IntegrationTypeEnums.GOOGLE_DRIVE && i.isActive,
      )
    },
  },
  methods: {
    connectGoogleDrive(): void {
      const wsStore = useWorkspaceStore()
      const intStore = useIntegrationStore()
      intStore
        .getAuthorizeUrl({
          workspaceId: wsStore.getSelectedWorkspaceId,
          integrationType: IntegrationNs.IntegrationTypeEnums.GOOGLE_DRIVE,
        })
        .then((url: string) => {
          window.location.href = url
        })
    },
    openDisconnectPopup(): void {
      const generalStore = useGeneralStore()
      const payload = {
        hasInputBox: false,
        content: {
          title: 'Disconnect Google Drive',
          message:
            '<p>Are you sure you want to disconnect <b>Google Drive</b> from this workspace? You will no longer be able to import files or export content to Drive.</p>',
        },
        callback: {
          content: 'Disconnect',
          function: () => this.disconnectGoogleDrive(),
        },
      } as PopupNs.Popup
      generalStore.showPopup(payload)
    },
    disconnectGoogleDrive(): void {
      const wsStore = useWorkspaceStore()
      const intStore = useIntegrationStore()
      intStore.removeIntegration({
        workspaceId: wsStore.getSelectedWorkspaceId,
        integrationType: IntegrationNs.IntegrationTypeEnums.GOOGLE_DRIVE,
      } as IntegrationNs.IntegrationInfoRequest)
    },
    onFileImported(): void {
      // hook for parent context if needed
    },
  },
})
</script>

<style lang="scss" scoped>
.google-drive-view {
  display: flex;
  flex-direction: column;
  gap: var(--space-16);

  &__browser-header {
    h5 {
      font-weight: 500;
      margin-bottom: var(--space-4);
    }
    p {
      font-size: var(--fs-12);
      color: var(--text-neutral);
    }
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
  }

  &__disconnect {
    display: flex;
    justify-content: flex-end;
    padding-top: var(--space-8);
    border-top: 1px solid var(--border-neutral);
    margin-top: var(--space-8);
  }
}
</style>
